// src/pages/Estabulacion.jsx
import React from "react";
import { Seo } from "../components/Seo";
import { BreadcrumbLd } from "../components/BreadcrumbLd";
import { usePrecios } from "../hooks/usePrecios";

const euros = (n) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

const PriceCard = ({ title, price, period = "/Mes", features = [], note }) => (
  <div className="col-md-4 col-sm-6">
    <div
      className="pricing-card"
      style={{
        padding: 24,
        borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        background: "#fff",
        marginBottom: 24,
      }}
    >
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <div style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 16px" }}>
        {price} <span style={{ fontSize: 16, fontWeight: 400 }}>{period}</span>
      </div>

      <ul
        style={{
          textAlign: "left",
          paddingLeft: 18,
          lineHeight: 1.6,
          marginBottom: 0,
        }}
      >
        {features.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>

      {note ? (
        <p style={{ fontSize: 12, opacity: 0.8, marginTop: 12 }}>
          <em>{note}</em>
        </p>
      ) : null}
      {/* Nota: los servicios adicionales se muestran en una card aparte */}
    </div>
  </div>
);

const ExtrasCard = ({ items = [] }) => (
  <div className="col-md-12">
    <div
      className="pricing-card"
      style={{
        padding: 24,
        borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        background: "#fff",
        marginBottom: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Servicios adicionales</h3>
        <span
          className="label label-info"
          style={{
            borderRadius: 999,
            fontSize: 12,
            padding: "4px 10px",
            verticalAlign: "middle",
          }}
        >
          Aplica a todas las modalidades
        </span>
      </div>

      <ul
        style={{
          textAlign: "left",
          paddingLeft: 18,
          lineHeight: 1.6,
          margin: "12px 0 0",
          columns: 1,
        }}
      >
        {items.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  </div>
);

export default function Estabulacion() {
  const { precios, error: preciosError } = usePrecios();
  const planes = precios?.Estabulacion?.planes || [];
  // Lista única de servicios adicionales (aplica a todas las opciones)
  const extrasGenerales = [
    "Doma de caballos",
    "Herraje",
    "Dentista",
    "Veterinario",
    "Cuerda",
    "D. Técnica",
    "Soltar a tu caballo entre semana",
  ];

  return (
    <div id="estabulacion" className="text-center">
      <Seo
        title="Estabulación | Pupilaje y cuidados para tu caballo"
        description="Boxes amplios, paddocks y atención diaria. Pupilaje en entorno seguro con personal experimentado."
        path="/estabulacion"
        image="https://madridcampogrande.es/og/default.jpg"
      />
      <BreadcrumbLd
        items={[
          { name: "Inicio", path: "/" },
          { name: "Estabulación", path: "/estabulacion" },
        ]}
      />

      <div className="container">
        <div className="section-title" style={{ marginBottom: 16 }}>
          <h2 style={{ marginBottom: 8 }}>Estabulación</h2>
          <p style={{ maxWidth: 820, margin: "0 auto" }}>
            Cuidar tu caballo es nuestra prioridad.
          </p>
        </div>

        <div>
          <img
            src="/img/portfolio/estabulacion-1.jpg"
            alt="Estabulación"
            style={{
              maxWidth: "40%",
              height: "auto",
              marginBottom: 24,
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </div>

        <div className="row" style={{ marginBottom: 24 }}>
          <div className="col-md-10 col-md-offset-1">
            <p style={{ fontSize: 16 }}>
              En Hípica Madrid Campogrande contamos con{" "}
              <strong>58 boxes</strong> con bebedero automático. La cama es de
              paja o viruta y su limpieza es diaria. Nuestra hípica tiene un
              fácil acceso y permite salir a disfrutar del campo.
            </p>
          </div>
        </div>

        {preciosError && (
          <div className="alert alert-warning">
            No se han podido cargar los precios en este momento.
          </div>
        )}

        {!precios && !preciosError && <p>Cargando precios...</p>}

        <div className="row">
          {planes[0] && (
            <PriceCard
              title={planes[0].title}
              price={euros(planes[0].price)}
              period={planes[0].period}
              features={[
              "Un año de clases en grupo gratuito*",
              "Uso de las instalaciones",
              "3 comidas diarias más forraje",
              "Servicio de guadarnés",
              "Cama de paja o viruta",
              "Ayuda del mozo para preparar tu caballo",
              "Limpieza diaria y mantenimiento de la cuadra",
              "Control sanitario",
              "Veterinario y herrajes no incluidos",
              ]}
              note="* Las clases serán en grupo ya formado y dependiendo de la capacidad de pista. Las clases particulares no están incluidas; consultar con el profesor (coste adicional)."
            />
          )}

          {planes[1] && (
            <PriceCard
              title={planes[1].title}
              price={euros(planes[1].price)}
              period={planes[1].period}
              features={[
              "Uso de las instalaciones",
              "Servicio de guadarnés",
              "3 comidas diarias más forraje",
              "Cuidados de mozo",
              "Refugio",
              "Bebederos automáticos",
              ]}
            />
          )}

          {planes[2] && (
            <PriceCard
              title={planes[2].title}
              price={euros(planes[2].price)}
              period={planes[2].period}
              features={[
              "Uso de las instalaciones",
              "3 comidas diarias más forraje",
              "Cuidados de mozo",
              "Refugio",
              "Bebedero automático",
              "Vallado eléctrico",
              ]}
            />
          )}
        </div>

        {/* Card independiente de Servicios adicionales (aplica a todos) */}
        <div className="row">
          <ExtrasCard items={extrasGenerales} />
        </div>
      </div>
    </div>
  );
}
